/**
 * Phase 23 (Plan 23-03) — Operational Dashboard page.
 *
 * Replaces the Phase-3 health pill + DemoForm at `/dashboard`. The legacy
 * health surface lives at `/diagnostics` (Super Admin only) per D-243.
 *
 * Layout (D-241 12-col responsive grid):
 *   - PendingForMe / MyWorkOrders / AlertsRecent → md:col-span-4
 *   - Throughput stat cards (x4) → md:col-span-3   (client feedback 2026-09)
 *   - LogsheetThroughput → md:col-span-12   (own row — client feedback 2026-09)
 *   - WorkOrderStatus → md:col-span-4
 *   - KpiRollup → md:col-span-8
 *   - SiteCompletion → md:col-span-12
 *
 * Client feedback (2026-09) asked for Logsheet Throughput as "a separate card".
 * It was already its own WidgetCard, but at md:col-span-8 it shared a row with
 * WorkOrderStatus and the two read as one block. Throughput now owns a full
 * row; WorkOrderStatus keeps its designed 4-wide donut and pairs with KpiRollup
 * (12 → 8) so the row below stays full rather than leaving an 8-column gap.
 *
 * Follow-up on the same feedback — "separate cards for separate legends": a row
 * of four stat cards (one per chart series) sits above the chart, each showing
 * that status's total for the selected window. They are NOT lazy: no recharts
 * dependency, so they render while the chart chunk is still downloading.
 *
 * D-240 PRIMARY CONTRACT — Chart-bearing widgets (ThroughputWidget,
 * WorkOrderStatusWidget, KpiRollupWidget) are wrapped in `React.lazy(...)` so
 * they emit separate async chunks at build time. Combined with the recharts
 * `manualChunks` split from plan 23-02, the recharts library only downloads
 * when this branch renders. The non-chart-bearing widgets (PendingForMe,
 * MyWorkOrders, AlertsRecent, SiteCompletion) remain synchronous imports —
 * they are tiny and have no recharts dependency.
 *
 * Page-level branches: loading skeleton → page error → empty state → populated.
 * Widget-level branches still apply inside each widget's 4-state contract
 * (skeleton/empty/error/populated) per plan 23-02.
 */

import { useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useState, type JSX } from 'react';

import { AlertsRecentWidget } from '@/components/dashboard/AlertsRecentWidget';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { MyWorkOrdersWidget } from '@/components/dashboard/MyWorkOrdersWidget';
import { PendingForMeWidget } from '@/components/dashboard/PendingForMeWidget';
import { SiteCompletionWidget } from '@/components/dashboard/SiteCompletionWidget';
import { ThroughputStatCards } from '@/components/dashboard/ThroughputStatCards';
import { WidgetSkeleton } from '@/components/dashboard/widget-states';
import { Button } from '@/components/ui/button';
import { useDashboardSummary } from '@/hooks/useDashboardSummary';
import {
  clampThroughputDays,
  readThroughputDays,
  writeThroughputDays,
} from '@/lib/storage/throughput-window';

// D-240: each lazy() call produces a separate async chunk in dist/stats.json,
// separate from the initial-entry chunk and from the recharts vendor chunk.
const ThroughputWidget = lazy(() => import('@/components/dashboard/ThroughputWidget'));
const WorkOrderStatusWidget = lazy(
  () => import('@/components/dashboard/WorkOrderStatusWidget'),
);
const KpiRollupWidget = lazy(() => import('@/components/dashboard/KpiRollupWidget'));

/** Skeleton grid used during the initial loading branch — mirrors the populated layout. */
function DashboardSkeletonGrid(): JSX.Element {
  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-12"
      data-testid="dashboard-page-skeleton"
    >
      <div className="md:col-span-4">
        <WidgetSkeleton testId="dashboard-skeleton-pending" rows={3} />
      </div>
      <div className="md:col-span-4">
        <WidgetSkeleton testId="dashboard-skeleton-mywos" rows={3} />
      </div>
      <div className="md:col-span-4">
        <WidgetSkeleton testId="dashboard-skeleton-alerts" rows={3} />
      </div>
      {[1, 2, 3, 4].map((n) => (
        <div className="md:col-span-3" key={n}>
          <WidgetSkeleton testId={`dashboard-skeleton-throughput-stat-${String(n)}`} rows={1} />
        </div>
      ))}
      <div className="md:col-span-12">
        <WidgetSkeleton testId="dashboard-skeleton-throughput" rows={4} />
      </div>
      <div className="md:col-span-4">
        <WidgetSkeleton testId="dashboard-skeleton-wo-status" rows={3} />
      </div>
      <div className="md:col-span-8">
        <WidgetSkeleton testId="dashboard-skeleton-kpi-rollup" rows={4} />
      </div>
      <div className="md:col-span-12">
        <WidgetSkeleton testId="dashboard-skeleton-sites" rows={3} />
      </div>
    </div>
  );
}

export function Dashboard(): JSX.Element {
  const qc = useQueryClient();
  // Issue #6: user-selectable throughput window — persisted per-viewer, clamped 7-90,
  // and part of the summary query key so changing it re-fetches for the new window.
  const [throughputDays, setThroughputDays] = useState<number>(() => readThroughputDays());
  const { data, isLoading, isFetching, isPlaceholderData, isError, error, refetch } =
    useDashboardSummary(throughputDays);

  const onRefresh = (): void => {
    // Prefix match — invalidates ['dashboard-summary', N] for every window.
    void qc.invalidateQueries({ queryKey: ['dashboard-summary'] });
  };

  const onThroughputDaysChange = (days: number): void => {
    const clamped = clampThroughputDays(days);
    setThroughputDays(clamped);
    writeThroughputDays(clamped);
  };

  // Page-level loading — true initial load only (no data of any window yet).
  // A window switch keeps the page rendered: `placeholderData: keepPreviousData`
  // in useDashboardSummary serves the previous window's summary while the new
  // one loads, and `isPlaceholderData` gates just the throughput widget below.
  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <DashboardHeader
          generatedAt={undefined}
          isFetching={isFetching}
          onRefresh={onRefresh}
        />
        <DashboardSkeletonGrid />
      </div>
    );
  }

  // Page-level error.
  if (isError) {
    return (
      <div className="space-y-6">
        <DashboardHeader
          generatedAt={undefined}
          isFetching={isFetching}
          onRefresh={() => {
            void refetch();
          }}
        />
        <div
          className="border-destructive/50 bg-destructive/10 text-destructive rounded-md border p-4 text-sm"
          data-testid="dashboard-page-error"
        >
          <strong>Error {String(error.status)}:</strong> {error.message}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-3"
            onClick={() => {
              void refetch();
            }}
            data-testid="dashboard-page-error-retry-btn"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (data === undefined) {
    // TanStack Query never reaches this branch when isLoading=false and
    // isError=false (data is guaranteed) — defensive return for narrow types.
    return <></>;
  }

  // Page-level empty — every sub-block is null.
  const anyWidget =
    data.pendingForMe !== null ||
    data.myWorkOrders !== null ||
    data.logsheetThroughput !== null ||
    data.workOrderStatus !== null ||
    data.kpiRollup !== null ||
    data.alertsRecent !== null ||
    data.siteCompletion !== null;

  if (!anyWidget) {
    return (
      <div className="space-y-6">
        <DashboardHeader
          generatedAt={data.generatedAt}
          isFetching={isFetching}
          onRefresh={onRefresh}
        />
        <div
          className="text-muted-foreground py-16 text-center"
          data-testid="dashboard-empty"
        >
          No operational data yet — your account isn&rsquo;t scoped to any active work yet.
          Ping your admin.
        </div>
      </div>
    );
  }

  // Populated render. Each widget receives its block + the per-page retry/loader
  // shape (the page-level error is already short-circuited above; widgets
  // surface their own per-block error/empty branches if/when those become
  // distinguishable from the bundled response).
  const onWidgetRetry = (): void => {
    void refetch();
  };

  return (
    <div className="space-y-6">
      <DashboardHeader
        generatedAt={data.generatedAt}
        isFetching={isFetching}
        onRefresh={onRefresh}
      />
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-12"
        data-testid="dashboard-grid"
      >
        {data.pendingForMe !== null && (
          <PendingForMeWidget
            data={data.pendingForMe}
            isLoading={false}
            error={null}
            onRetry={onWidgetRetry}
            className="md:col-span-4"
          />
        )}
        {data.myWorkOrders !== null && (
          <MyWorkOrdersWidget
            data={data.myWorkOrders}
            isLoading={false}
            error={null}
            onRetry={onWidgetRetry}
            className="md:col-span-4"
          />
        )}
        {data.alertsRecent !== null && (
          <AlertsRecentWidget
            data={data.alertsRecent}
            isLoading={false}
            error={null}
            onRetry={onWidgetRetry}
            className="md:col-span-4"
          />
        )}
        {/*
         * Client feedback (2026-09): one card per chart legend, above the chart.
         * Rendered OUTSIDE the Suspense boundary below — they carry no recharts
         * import, so the numbers appear without waiting on the chart chunk.
         */}
        {data.logsheetThroughput !== null && (
          <ThroughputStatCards
            data={data.logsheetThroughput}
            days={throughputDays}
            isLoading={isPlaceholderData}
            className="md:col-span-3"
          />
        )}
        {/*
         * D-240: chart-bearing widgets are rendered inside a single Suspense
         * boundary. Each is a React.lazy module → emits a separate async chunk
         * → recharts only downloads when this branch renders.
         */}
        <Suspense
          fallback={
            <div
              className="md:col-span-12"
              data-testid="dashboard-charts-suspense-fallback"
            >
              <WidgetSkeleton testId="dashboard-charts-skeleton" rows={4} />
            </div>
          }
        >
          {data.logsheetThroughput !== null && (
            <ThroughputWidget
              data={data.logsheetThroughput}
              days={throughputDays}
              onDaysChange={onThroughputDaysChange}
              // Issue #6: during a window switch `data` is the PREVIOUS window's
              // placeholder — skeleton this widget so its title (new window)
              // never sits above the old window's bars. The other widgets keep
              // showing the previous data; their blocks are window-independent.
              isLoading={isPlaceholderData}
              error={null}
              onRetry={onWidgetRetry}
              className="md:col-span-12"
            />
          )}
          {data.workOrderStatus !== null && (
            <WorkOrderStatusWidget
              data={data.workOrderStatus}
              isLoading={false}
              error={null}
              onRetry={onWidgetRetry}
              className="md:col-span-4"
            />
          )}
          {data.kpiRollup !== null && (
            <KpiRollupWidget
              data={data.kpiRollup}
              isLoading={false}
              error={null}
              onRetry={onWidgetRetry}
              className="md:col-span-8"
            />
          )}
        </Suspense>
        {data.siteCompletion !== null && (
          <SiteCompletionWidget
            data={data.siteCompletion}
            isLoading={false}
            error={null}
            onRetry={onWidgetRetry}
            className="md:col-span-12"
          />
        )}
      </div>
    </div>
  );
}
